import { PaymentTerminalV2 } from "@lemmonhq/types";
import { getAPIClient } from "../helpers";

// Types for the terminal management API
export interface TerminalFilters {
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CreateTerminalData {
  name: string;
  password?: string;
  client_id?: string;
  client_secret?: string;
  terminal_id?: number;
  staff_ids?: number[];
}

export interface UpdateTerminalData {
  name?: string;
  password?: string;
  client_id?: string;
  client_secret?: string;
  terminal_id?: number;
  staff_ids?: number[];
}

export const terminalsAPI = {
  // Terminal management functions following REST architecture
  getTerminalsV2: async (
    filters?: TerminalFilters,
  ): Promise<PaymentTerminalV2[]> => {
    const hc = getAPIClient();
    const params = new URLSearchParams();

    if (filters?.search) params.append("search", filters.search);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());

    const resp = await hc.api.v2.terminals.$get({
      query: Object.fromEntries(params.entries()),
    });

    if (!resp.ok) {
      throw new Error("Failed to fetch terminals");
    }

    const json = await resp.json();
    return json as PaymentTerminalV2[];
  },

  getTerminal: async (id: number): Promise<PaymentTerminalV2> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.terminals[":id"].$get({
      param: { id: id.toString() },
    });

    if (!resp.ok) {
      throw new Error("Failed to fetch terminal");
    }

    const json = await resp.json();
    return json as PaymentTerminalV2;
  },

  createTerminal: async (
    data: CreateTerminalData,
  ): Promise<PaymentTerminalV2> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.terminals.$post({
      json: data,
    });

    if (!resp.ok) {
      throw new Error("Failed to create terminal");
    }

    const json = await resp.json();
    return json as PaymentTerminalV2;
  },

  updateTerminal: async (
    id: number,
    data: UpdateTerminalData,
  ): Promise<PaymentTerminalV2> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.terminals[":id"].$patch({
      param: { id: id.toString() },
      json: data,
    });

    if (!resp.ok) {
      throw new Error("Failed to update terminal");
    }

    const json = await resp.json();
    return json as PaymentTerminalV2;
  },

  deleteTerminal: async (
    id: number,
  ): Promise<{ success: boolean; message: string }> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.terminals[":id"].$delete({
      param: { id: id.toString() },
    });

    if (!resp.ok) {
      throw new Error("Failed to delete terminal");
    }

    const json = await resp.json();
    return json as { success: boolean; message: string };
  },
};
